import {
  createCatalogEndpoint,
  createCrudEndpoints,
  createExportEndpoint,
  createNestedCrudEndpoints,
  createPrintEndpoint,
} from '@/core/utils'
import {
  TBundle,
  TCoilInventories,
  TCoilInventory,
  TCoilPayload,
  TDailyReport,
  TLog,
  TProject,
} from '.'
import {
  DataWithPagination,
  ShowPayload,
  TBaseCatalogs,
  TFilters,
} from '@/core/types'
import api from '@/core/config/axios'
import { MaybeRef, toValue } from 'vue'
import { TCoil, TCoils } from '../receptions'
import { AxiosResponse } from 'axios'
import { TCustomer } from '../customers'

export type TProjects = DataWithPagination<TProject>
export type TBundles = DataWithPagination<TBundle>
export type TDailyReports = DataWithPagination<TDailyReport>
export type TCoilsReports = DataWithPagination<TCoil>
export type TLogs = DataWithPagination<TLog>
type NestedParams = {
  modelId?: MaybeRef<number | string>
  modelChildId?: MaybeRef<number | string>
}

export const serviceConfig = {
  model: 'projects',
  version: 'v1',
  bundles: {
    model: 'bundles',
    version: 'v1',
  },
  coils: {
    model: 'coils',
    version: 'v1',
  },
  catalogs: {
    categories: {
      model: undefined,
      version: 'v1',
    },
    customers: {
      model: 'customers',
      version: 'v1',
    },
  },
  logsProduction: {
    model: 'log-reports',
    version: 'v1',
  },
  logDetails: {
    model: 'details',
    version: 'v1',
  },
  coilStocks: {
    model: 'coil-stocks',
    version: 'v1',
  },
}
const { categories, customers } = serviceConfig.catalogs

const moduleServiceHandlers = {
  ...createCrudEndpoints<TProjects, TProject>(
    serviceConfig.model,
    serviceConfig.version,
  ),
  loadHeaders: async (payload: ShowPayload): Promise<TProject> =>
    api
      .get(
        `${serviceConfig.version ?? ''}/${serviceConfig.model}/${toValue(
          payload.id,
        )}/detail-data`,
      )
      .then((response) => response.data),
  addCoils: async (
    payload: TCoilPayload,
    params: NestedParams,
  ): Promise<TProject> => {
    return api
      .post(
        `${serviceConfig.version ?? ''}/${serviceConfig.model}/${toValue(
          params?.modelId,
        )}/coils`,
        payload,
      )
      .then((response) => response.data)
  },
  printRecord: async (id: number, type: string, format: string = 'pdf') => {
    try {
      const response = await api.get(
        `${serviceConfig.version ?? ''}/${
          serviceConfig.model
        }/${id}/${type}/print`,
        {
          responseType: 'blob',
        },
      )

      download(response, format, type)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  },
  loadBundlesProject: async (
    params: NestedParams,
  ): Promise<Partial<TBundle>[]> =>
    await api
      .get(
        `${serviceConfig.version ?? ''}/${serviceConfig.model}/${toValue(
          params.modelId,
        )}/${serviceConfig.bundles.model}`,
      )
      .then((response) => response.data),
  loadBundlesFabricated: async (
    params: TFilters & NestedParams,
  ): Promise<TBundles> => {
    return api
      .get(
        `${serviceConfig.version ?? ''}/${serviceConfig.model}/${toValue(
          params.modelId,
        )}/fabricated-bundles`,
      )
      .then((response) => response.data)
  },
  loadUsedCoils: async (params: NestedParams): Promise<TCoilsReports> =>
    await api
      .get(
        `${serviceConfig.version ?? ''}/${serviceConfig.model}/${toValue(
          params.modelId,
        )}/used-coils`,
      )
      .then((response) => response.data),
}

const coilStocksHandlers = createCatalogEndpoint<TCoils>(
  serviceConfig.coilStocks.model,
  serviceConfig.coilStocks.version,
)

const bundleServiceHandlers = {
  ...createCrudEndpoints<TBundles, TBundle>(
    serviceConfig.bundles.model,
    serviceConfig.bundles.version,
  ),
  loadRecords: async (params: TFilters & NestedParams): Promise<TBundles> => {
    return api
      .get(
        `${serviceConfig.version ?? ''}/${serviceConfig.model}/${toValue(
          params.modelId,
        )}/${serviceConfig.bundles.model}`,
      )
      .then((response) => response.data)
  },
}

const moduleServiceDailyReportHandlers = {
  ...createCrudEndpoints<TDailyReports, TDailyReport>(
    serviceConfig.logsProduction.model,
    serviceConfig.logsProduction.version,
  ),
  loadRecord: async (
    params: TFilters & NestedParams,
  ): Promise<TDailyReport> => {
    return api
      .get(
        `${serviceConfig.version ?? ''}/${
          serviceConfig.logsProduction.model
        }/${toValue(params.modelChildId)}`,
      )
      .then((response) => response.data)
  },
}

const logsServiceHandlers = createNestedCrudEndpoints<TLogs, TLog>(
  serviceConfig.logsProduction.model,
  serviceConfig.logDetails.model,
  serviceConfig.logDetails.version,
)

const reportsServiceHandlers = createNestedCrudEndpoints<
  TDailyReports,
  TDailyReport
>(
  serviceConfig.model,
  serviceConfig.logsProduction.model,
  serviceConfig.version,
)

const moduleExportHandlers = createExportEndpoint(serviceConfig.model)

const categoriesCatalog = createCatalogEndpoint<TBaseCatalogs>(
  categories.model,
  categories.version,
)

const download = (response: AxiosResponse, format: string, model: string) => {
  const filename = `${model}.${format}`

  const blob = new Blob([response.data], { type: 'application/octet-stream' })

  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
}

const customersServiceHandlers = createCatalogEndpoint<TCustomer[]>(
  customers.model,
  customers.version,
)

const catalogServiceHandlers = {
  categoriesCatalog,
  customersServiceHandlers,
}

const coilsInventoryServiceHandlers = {
  ...createCrudEndpoints<TCoilInventories, TCoilInventory>(
    serviceConfig.coilStocks.model,
    serviceConfig.coilStocks.version,
  ),
}

const modulePrintHandlers = createPrintEndpoint(
  serviceConfig.coilStocks.model,
  serviceConfig.version,
)

export {
  moduleServiceHandlers,
  moduleExportHandlers,
  catalogServiceHandlers,
  moduleServiceDailyReportHandlers,
  logsServiceHandlers,
  bundleServiceHandlers,
  reportsServiceHandlers,
  coilStocksHandlers,
  coilsInventoryServiceHandlers,
  modulePrintHandlers,
}
